// SPDX-FileCopyrightText: 2026 Povl Filip Sonne-Frederiksen
//
// SPDX-License-Identifier: GPL-3.0-or-later

/**
 * Rediger › Geometri's measuring (frontend-u0o.11, slice S9; design.md §4,
 * after SketchUp and Revit), the geometry only:
 *
 *  - Mål (M), the tape measure: from a point it measures point to point;
 *    from an edge (a wall's axis, a plane trace, a guide) square to that
 *    edge's line. With Ctrl it leaves a guide instead — from an edge a guide
 *    line parallel to it, from a point a guide point — at the pointer or at
 *    a typed distance;
 *  - guides as the inference engine's snap targets (`inference.ts`): a
 *    guide line is infinite, so it is clipped far out to a segment;
 *  - Målsætning (⇧M), a persistent dimension chain: points picked in turn,
 *    measured along one direction (aligned with the first two, or the
 *    storey's red / green axis), drawn at an offset;
 *  - a selected room's temporary dimensions: each edge's length, set just
 *    inside the room, its area and perimeter.
 *
 * World x/y, metres. Pure: no React, no DOM.
 */

import type { SolidSpace } from '../api/types';
import { axes, type Inference, type SnapPoint, type SnapSegment } from './inference';
import type { XY } from './planWalls';
import { ringArea, spaceArea } from './spaces';

const sub = (a: XY, b: XY): XY => [a[0] - b[0], a[1] - b[1]];
const add = (a: XY, b: XY): XY => [a[0] + b[0], a[1] + b[1]];
const mul = (a: XY, k: number): XY => [a[0] * k, a[1] * k];
const dot = (a: XY, b: XY) => a[0] * b[0] + a[1] * b[1];
const len = (a: XY) => Math.hypot(a[0], a[1]);
const dist = (a: XY, b: XY) => Math.hypot(a[0] - b[0], a[1] - b[1]);
const unit = (a: XY): XY | null => {
  const L = len(a);
  return L > 1e-9 ? [a[0] / L, a[1] / L] : null;
};
/** The left normal of a unit direction. */
const left = (d: XY): XY => [-d[1], d[0]];

/** Shorter than this (m) counts as nothing: no guide on the edge itself, no stretch of zero. */
const EPS = 1e-4;

/** A direction this close to the storey's red / green axis is taken as the axis (scan noise, not intent). */
export const AXIS_SNAP = (5 * Math.PI) / 180;

/** Unit `d` turned onto the nearest of the storey's four axis directions when within `AXIS_SNAP`. */
export function snapToAxes(d: XY, axisAngle: number): XY {
  const { x, y } = axes(axisAngle);
  for (const a of [x, y, mul(x, -1), mul(y, -1)]) {
    if (dot(d, a) >= Math.cos(AXIS_SNAP)) return a;
  }
  return d;
}

// ---------------------------------------------------------------- guides --

/** A guide line: through `a`, along unit `dir`, infinite. */
export interface GuideLine {
  id: string;
  kind: 'line';
  /** The storey it was drawn on (null: the plan showed every storey). */
  storeyId: number | null;
  a: XY;
  dir: XY;
}

/** A guide point. */
export interface GuidePoint {
  id: string;
  kind: 'point';
  storeyId: number | null;
  p: XY;
}

export type Guide = GuideLine | GuidePoint;

/** A guide as made, before it has an id. */
export type NewGuide = Omit<GuideLine, 'id'> | Omit<GuidePoint, 'id'>;

/** How far a guide line reaches each way from its anchor, m (well past any building). */
export const GUIDE_REACH = 200;

export function guideLineEnds(g: Pick<GuideLine, 'a' | 'dir'>, reach = GUIDE_REACH): [XY, XY] {
  return [add(g.a, mul(g.dir, -reach)), add(g.a, mul(g.dir, reach))];
}

/** Shown on the plan of storey `storeyId` (null: every storey is shown, so all are). */
export const onStorey = <T extends { storeyId: number | null }>(items: readonly T[], storeyId: number | null): T[] =>
  storeyId === null ? [...items] : items.filter((g) => g.storeyId === storeyId);

/** The 1-based number a guide or chain goes by ("Hjælpelinje 3"): its place in the list. */
export function guideLabel(guides: readonly Guide[], g: Guide): string {
  return `${g.kind === 'line' ? 'Hjælpelinje' : 'Hjælpepunkt'} ${guides.indexOf(g) + 1}`;
}

/** The guides on the shown storey as the inference engine's segments and points. */
export function guideSnaps(guides: readonly Guide[], storeyId: number | null): { segments: SnapSegment[]; points: SnapPoint[] } {
  const segments: SnapSegment[] = [];
  const points: SnapPoint[] = [];
  for (const g of guides) {
    if (storeyId !== null && g.storeyId !== storeyId) continue;
    const label = guideLabel(guides, g);
    if (g.kind === 'line') {
      const [a, b] = guideLineEnds(g);
      segments.push({ a, b, source: 'guide', label });
    } else points.push({ p: g.p, label });
  }
  return { segments, points };
}

// ---------------------------------------------------------- tape measure --

/** Where the tape was hooked: a point, and the edge's line when it was hooked on one. */
export interface TapeStart {
  p: XY;
  line: [XY, XY] | null;
  /** What it was hooked on ("Væg 1", "Endepunkt · Væg 1"), for the readout; null when free. */
  label: string | null;
}

/**
 * The inference at the first click: on an edge, trace or guide the tape
 * measures from that line — with `axisAngle`, an edge within `AXIS_SNAP` of
 * the storey's axes is taken as the axis through the hook (a scanned trace
 * a few degrees off is noise; a guide parallel to it would cross the room).
 */
export function tapeStart(inf: Inference, axisAngle: number | null = null): TapeStart {
  const s = inf.segment;
  if (s && (inf.kind === 'on-edge' || inf.kind === 'trace' || inf.kind === 'guide')) {
    const d = unit(sub(s.b, s.a));
    if (d && axisAngle !== null) {
      const a = snapToAxes(d, axisAngle);
      if (a !== d) return { p: inf.point, line: [inf.point, add(inf.point, a)], label: s.label };
    }
    return { p: inf.point, line: [s.a, s.b], label: s.label };
  }
  return { p: inf.point, line: null, label: inf.tip || null };
}

export interface TapeReading {
  /** 'edge': square to the start's line; 'point': point to point. */
  mode: 'edge' | 'point';
  /** The measured line's ends: the start point (or the foot on the edge) and the pointer. */
  from: XY;
  to: XY;
  value: number;
  /** The run along world x and y. */
  dx: number;
  dy: number;
}

/** The foot of `p` on the infinite line through `line`. */
function foot(line: [XY, XY], p: XY): XY {
  const d = unit(sub(line[1], line[0]));
  if (!d) return line[0];
  return add(line[0], mul(d, dot(sub(p, line[0]), d)));
}

/** What the tape reads with the pointer at `p`. */
export function tapeReading(start: TapeStart, p: XY): TapeReading {
  const from = start.line ? foot(start.line, p) : start.p;
  return { mode: start.line ? 'edge' : 'point', from, to: p, value: dist(from, p), dx: p[0] - from[0], dy: p[1] - from[1] };
}

/**
 * The guide the tape leaves at pointer `p` (Ctrl, SketchUp): from an edge a
 * line parallel to it, through `p` or at `typed` metres on `p`'s side (the
 * left side when `p` is on the edge); from a point a guide point at `p`, or
 * `typed` metres towards it. Null when it would sit on the start.
 */
export function tapeGuide(start: TapeStart, p: XY, typed: number | null, storeyId: number | null): NewGuide | null {
  if (start.line) {
    const dir = unit(sub(start.line[1], start.line[0]));
    if (!dir) return null;
    const f = foot(start.line, p);
    const n = left(dir);
    const side = dot(sub(p, f), n);
    const d = typed ?? Math.abs(side);
    if (!(d > EPS)) return null;
    return { kind: 'line', storeyId, a: add(f, mul(n, side < -EPS ? -d : d)), dir };
  }
  const v = sub(p, start.p);
  const L = len(v);
  if (typed === null) return L > EPS ? { kind: 'point', storeyId, p } : null;
  if (!(typed > EPS)) return null;
  const dir = unit(v) ?? [1, 0];
  return { kind: 'point', storeyId, p: add(start.p, mul(dir, typed)) };
}

// ------------------------------------------------------- dimension chains --

/** A persistent dimension chain (Målsætning): `points` measured along unit `dir`, the line `offset` m to its left. */
export interface DimChain {
  id: string;
  storeyId: number | null;
  points: XY[];
  dir: XY;
  offset: number;
}

export type NewDimChain = Omit<DimChain, 'id'>;

/** How a chain measures: along its first two points, or the storey's red (x) / green (y) axis. */
export type DimOrient = 'aligned' | 'x' | 'y';

/**
 * The chain's direction. 'aligned': square to `ref` — the edge the first
 * point was picked on (Revit: a dimension to walls measures across them) —
 * else along the first two distinct points (null until there are two); in
 * either case within `AXIS_SNAP` of an axis it is the axis. 'x' / 'y': the
 * storey's red / green axis.
 */
export function dimDirection(points: readonly XY[], orient: DimOrient, axisAngle: number, ref: XY | null = null): XY | null {
  if (orient === 'x') return points.length >= 2 || ref ? axes(axisAngle).x : null;
  if (orient === 'y') return points.length >= 2 || ref ? axes(axisAngle).y : null;
  const r = ref ? unit(ref) : null;
  if (r) {
    // The normal pointing the way the chain runs (left of the edge when there is no second point yet).
    let n = left(r);
    const run = points.length >= 2 ? sub(points[points.length - 1], points[0]) : null;
    if (run && dot(run, n) < 0) n = mul(n, -1);
    return snapToAxes(n, axisAngle);
  }
  for (let i = 1; i < points.length; i += 1) {
    const d = unit(sub(points[i], points[0]));
    if (d) return snapToAxes(d, axisAngle);
  }
  return null;
}

/** The pointer's signed distance from the line through the first point along `dir` (positive to its left). */
export function dimOffset(points: readonly XY[], dir: XY, pointer: XY): number {
  return dot(sub(pointer, points[0]), left(dir));
}

export interface DimStretch {
  from: XY;
  to: XY;
  /** Where the figure goes: the stretch's middle on the dimension line. */
  at: XY;
  value: number;
}

export interface DimGeometry {
  /** The dimension line, end to end. */
  line: [XY, XY];
  /** Where each station meets the line (one per distinct station, in order along `dir`). */
  ticks: XY[];
  /** Each point's witness line, from the point to the dimension line (in the chain's point order). */
  witness: [XY, XY][];
  stretches: DimStretch[];
  /** End to end. */
  total: number;
}

/** A chain's drawing: its points projected on the line, sorted, a figure per stretch. */
export function dimGeometry(chain: Pick<DimChain, 'points' | 'dir' | 'offset'>): DimGeometry {
  const { points, dir, offset } = chain;
  const n = left(dir);
  const o = points[0];
  const base = add(o, mul(n, offset));
  const on = (t: number): XY => add(base, mul(dir, t));
  const stations = points.map((p) => dot(sub(p, o), dir));
  const sorted = [...stations].sort((a, b) => a - b);
  const distinct: number[] = [];
  for (const t of sorted) if (distinct.length === 0 || t - distinct[distinct.length - 1] > 5e-4) distinct.push(t);
  const witness = points.map((p, i): [XY, XY] => [p, on(stations[i])]);
  const stretches: DimStretch[] = [];
  for (let i = 0; i + 1 < distinct.length; i += 1) {
    const a = distinct[i];
    const b = distinct[i + 1];
    stretches.push({ from: on(a), to: on(b), at: on((a + b) / 2), value: round(b - a) });
  }
  const lo = distinct[0] ?? 0;
  const hi = distinct[distinct.length - 1] ?? 0;
  return { line: [on(lo), on(hi)], ticks: distinct.map(on), witness, stretches, total: round(hi - lo) };
}

/** To a tenth of a millimetre: a figure never reads 2,4999999. */
const round = (v: number) => Math.round(v * 1e4) / 1e4;

// ------------------------------------------- a selected room's dimensions --

export interface SpaceTempDims {
  /** One per outer edge long enough to carry a figure, in ring order, set `inset` m inside the room (negative: outside). */
  edges: DimStretch[];
  /** m² (the stored area, else the polygon's). */
  area: number;
  /** m, of the outer ring. */
  perimeter: number;
}

/**
 * A selected room's temporary dimensions: each edge of its outer ring with
 * its length, drawn `inset` metres inside it (negative: outside, over the
 * walls it sits against — Revit's place, clear of the room's own label),
 * skipping edges shorter than `minEdge`; and its area and perimeter.
 */
export function spaceTempDims(space: Pick<SolidSpace, 'polygon' | 'area'>, inset: number, minEdge = 0.05): SpaceTempDims {
  const ring = space.polygon.outer as readonly XY[];
  // Counter-clockwise: the inside is to the left of each edge.
  const ccw = ringArea(ring as XY[]) > 0;
  const edges: DimStretch[] = [];
  let perimeter = 0;
  for (let i = 0; i < ring.length; i += 1) {
    const a = ring[i];
    const b = ring[(i + 1) % ring.length];
    const L = dist(a, b);
    perimeter += L;
    if (L < minEdge) continue;
    const d = unit(sub(b, a))!;
    const k = mul(left(d), ccw ? inset : -inset);
    const from = add(a, k);
    const to = add(b, k);
    edges.push({ from, to, at: mul(add(from, to), 0.5), value: round(L) });
  }
  return { edges, area: spaceArea(space as SolidSpace), perimeter };
}
